import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  AssociationFields,
  Field,
  Section,
  Toggle,
} from './association-fields';
import {
  useAssociateItem,
  useCreateItemRequest,
  useUploadItemMedia,
  type BusinessItemAssociation,
  type ItemPrice,
  type ItemRequest,
} from './item-prices.api';

/**
 * Connecting a price to the catalogue, in its two forms.
 *
 * Both start from the price row, because the POS already knows most of the
 * answer — the fields arrive filled in and are corrected rather than typed.
 */

function associationFrom(
  price: ItemPrice,
  businessId: string | null
): BusinessItemAssociation {
  return {
    businessId,
    inStoreId: price.itemNumber,
    upc: price.upc,
    isVisible: price.isVisible,
    isAvailable: price.isAvailable,
    sectionId: price.sectionId,
    department: price.department,
    aisle: price.aisle,
    stateTaxPct: price.stateTaxPct,
    munTaxPct: price.munTaxPct,
    isAllNatural: price.isAllNatural,
    isLactoseFree: price.isLactoseFree,
    isSugarFree: price.isSugarFree,
    isKeto: price.isKeto,
    isOrganic: price.isOrganic,
    isVegan: price.isVegan,
    isVegetarian: price.isVegetarian,
    isGlutenFree: price.isGlutenFree,
    isGrassFed: price.isGrassFed,
    isAntioxidant: price.isAntioxidant,
    isNutFree: price.isNutFree,
    isNonGmo: price.isNonGmo,
    hasRandomWeight: price.hasRandomWeight,
    madeLocally: price.madeLocally,
    weightType: price.weightType,
    minQuantity: price.minQuantity,
    maxQuantity: price.maxQuantity,
    stepAmount: price.stepAmount,
    unitPriceFactor: price.unitPriceFactor,
    taxRule: price.taxRule,
    basePriceMethod: price.basePriceMethod,
    basePrice: price.basePrice,
    baseMultiple: price.baseMultiple,
    promoPrice: price.promoPrice,
    promoMultiple: price.promoMultiple,
    promoPriceMethod: price.promoPriceMethod,
    promoStartDate: price.promoStartDate,
    promoEndDate: price.promoEndDate,
    cost: price.cost,
    pack: price.pack,
  };
}

/** Image upload plus the URL it produced, for the two item pictures. */
function ImagePicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (url: string) => void;
}) {
  const { t } = useTranslation();
  const upload = useUploadItemMedia();
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <Field label={label}>
      <div className="space-y-2">
        {value && (
          <img
            src={value}
            alt=""
            className="bg-muted mx-auto max-h-32 rounded-lg object-contain"
          />
        )}
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            upload.mutate(file, {
              onSuccess: onChange,
              onError: () => toast.error(t('itemPrices.uploadFailed')),
            });
            event.target.value = '';
          }}
        />
        <Button
          variant="outline"
          className="w-full border-dashed"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {upload.isPending ? <Loader2 className="animate-spin" /> : <ImagePlus />}
          {t('itemPrices.uploadImage')}
        </Button>
      </div>
    </Field>
  );
}

/** Ask for an item that does not exist in the catalogue yet. */
export function ItemRequestSheet({
  price,
  businessId,
  open,
  onOpenChange,
  onSaved,
}: {
  price: ItemPrice | null;
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateItemRequest();

  const [draft, setDraft] = useState<ItemRequest | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);

  if (price && hydrated !== price.id) {
    setHydrated(price.id);
    setDraft({
      suggestedDepartment: price.department,
      internalRank: price.salesRank,
      gtin: price.gtin,
      brand: price.brand,
      name: price.name,
      description: price.description,
      itemImage: '',
      itemThumbnail: '',
      searchTags: price.searchTags,
      internalId: '',
      isPackagingSensitive: price.isPackagingSensitive,
      isTemperatureSensitive: price.isTemperatureSensitive,
      isAlcoholicBeverage: price.isAlcoholicBeverage,
      isPerishable: price.isPerishable,
      isEbt: price.isEbt,
      idRequired: price.idRequired,
      businessItemAssociation: associationFrom(price, businessId),
    });
  }

  if (!draft || !price) return null;

  const set = (patch: Partial<ItemRequest>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const setAssociation = (patch: Partial<BusinessItemAssociation>) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            businessItemAssociation: {
              ...current.businessItemAssociation,
              ...patch,
            },
          }
        : current
    );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('itemPrices.newItemRequest')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 p-4">
          <Section title={t('itemPrices.theItem')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="GTIN">
                <Input
                  value={draft.gtin ?? ''}
                  onChange={(e) => set({ gtin: e.target.value })}
                />
              </Field>
              <Field label={t('itemPrices.internalId')}>
                <Input
                  value={draft.internalId ?? ''}
                  onChange={(e) => set({ internalId: e.target.value })}
                />
              </Field>
            </div>

            <Field label={t('itemPrices.brand')}>
              <Input
                value={draft.brand ?? ''}
                onChange={(e) => set({ brand: e.target.value })}
              />
            </Field>
            <Field label={t('itemPrices.name')}>
              <Input
                value={draft.name ?? ''}
                onChange={(e) => set({ name: e.target.value })}
              />
            </Field>
            <Field label={t('itemPrices.description')}>
              <Input
                value={draft.description ?? ''}
                onChange={(e) => set({ description: e.target.value })}
              />
            </Field>
            <Field
              label={t('itemPrices.searchTags')}
              hint={t('itemPrices.searchTagsHint')}
            >
              <Textarea
                rows={2}
                value={draft.searchTags ?? ''}
                onChange={(e) => set({ searchTags: e.target.value })}
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <ImagePicker
                label={t('itemPrices.itemImage')}
                value={draft.itemImage}
                onChange={(itemImage) => set({ itemImage })}
              />
              <ImagePicker
                label={t('itemPrices.thumbnail')}
                value={draft.itemThumbnail}
                onChange={(itemThumbnail) => set({ itemThumbnail })}
              />
            </div>

            <div className="grid gap-x-4 sm:grid-cols-2">
              <Toggle
                label={t('itemPrices.packagingSensitive')}
                checked={draft.isPackagingSensitive}
                onChange={(isPackagingSensitive) => set({ isPackagingSensitive })}
              />
              <Toggle
                label={t('itemPrices.temperatureSensitive')}
                checked={draft.isTemperatureSensitive}
                onChange={(isTemperatureSensitive) =>
                  set({ isTemperatureSensitive })
                }
              />
              <Toggle
                label={t('itemPrices.alcoholicBeverage')}
                checked={draft.isAlcoholicBeverage}
                onChange={(isAlcoholicBeverage) => set({ isAlcoholicBeverage })}
              />
              <Toggle
                label={t('itemPrices.perishable')}
                checked={draft.isPerishable}
                onChange={(isPerishable) => set({ isPerishable })}
              />
              <Toggle
                label={t('itemPrices.isEbt')}
                checked={draft.isEbt}
                onChange={(isEbt) => set({ isEbt })}
              />
              <Toggle
                label={t('itemPrices.idRequired')}
                checked={draft.idRequired}
                onChange={(idRequired) => set({ idRequired })}
              />
            </div>
          </Section>

          <AssociationFields
            value={draft.businessItemAssociation}
            onChange={setAssociation}
            businessId={businessId}
          />

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={create.isPending || !draft.name?.trim()}
              onClick={() =>
                create.mutate(draft, {
                  onSuccess: () => {
                    toast.success(t('itemPrices.created'));
                    onOpenChange(false);
                    onSaved();
                  },
                  onError: () => toast.error(t('itemPrices.actionFailed')),
                })
              }
            >
              {create.isPending && <Loader2 className="animate-spin" />}
              {t('itemPrices.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Tie this price to an item — or a pending request — that already exists. */
export function AssociateSheet({
  price,
  target,
  businessId,
  open,
  onOpenChange,
  onSaved,
}: {
  price: ItemPrice | null;
  target: { itemId: string | null; itemRequestId: string | null; label: string } | null;
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const associate = useAssociateItem();

  const [draft, setDraft] = useState<BusinessItemAssociation | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);

  const key = price && target ? `${price.id}|${target.itemId ?? target.itemRequestId}` : null;

  if (price && key && hydrated !== key) {
    setHydrated(key);
    setDraft(associationFrom(price, businessId));
  }

  if (!draft || !price || !target) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('itemPrices.associate')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 p-4">
          <p className="bg-accent text-accent-foreground rounded-xl border p-3 text-sm">
            {t('itemPrices.associateEcho', {
              price: [price.brand, price.name].filter(Boolean).join(' '),
              item: target.label,
            })}
          </p>

          <AssociationFields
            value={draft}
            onChange={(patch) =>
              setDraft((current) => (current ? { ...current, ...patch } : current))
            }
            businessId={businessId}
          />

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={associate.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={associate.isPending}
              onClick={() =>
                associate.mutate(
                  {
                    itemId: target.itemId,
                    itemRequestId: target.itemRequestId,
                    association: draft,
                  },
                  {
                    onSuccess: () => {
                      toast.success(t('itemPrices.connectedToast'));
                      onOpenChange(false);
                      onSaved();
                    },
                    onError: () => toast.error(t('itemPrices.actionFailed')),
                  }
                )
              }
            >
              {associate.isPending && <Loader2 className="animate-spin" />}
              {t('itemPrices.associate')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
